import { Box, Typography, Button, useTheme, useMediaQuery } from '@mui/material';

const steps = [
  {
    week: 'Week 1',
    number: '01',
    title: 'Strategy & test grid',
    description:
      'We map your ICP, build your hook × angle × format matrix, and design the creative test grid before a single creator is briefed. Every video has a hypothesis tied to your conversion goal.',
  },
  {
    week: 'Week 2',
    number: '02',
    title: 'Creator sourcing & briefing',
    description:
      "We source, vet, contract, and brief creators matched to your target persona — from our retained roster, not a gig marketplace. Each creator produces 10+ videos across multiple angles. You get one review round. We handle the rest.",
  },
  {
    week: 'Week 3',
    number: '03',
    title: 'Warmed-account distribution',
    description:
      'Content goes out through pre-warmed, persona-matched TikTok accounts built for your ICP. Meta-ready cuts delivered hand-off to your existing paid agency at no extra cost.',
  },
  {
    week: 'Week 4+',
    number: '04',
    title: 'Attribution & iteration',
    description:
      'Per-creator deep links. Weekly variant leaderboard. Winners scale. Underperformers get cut. Reporting tuned to your conversion goal — installs, CPI, or blended ROAS.',
  },
];

export const LPSectionBrands = () => {
  const theme = useTheme();
  const isMdOrBelow = useMediaQuery(theme.breakpoints.down('md'));

  return (
    <Box
      id="how-it-works"
      sx={{
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        textAlign: 'center',
        mt: 2,
        pt: 10,
        pb: 10,
        px: { xs: 2, md: 4, lg: 4 }
      }}
    >
      {/* Section label */}
      <Typography
        fontFamily="Inter"
        sx={{
          fontSize: '12px',
          fontWeight: 600,
          letterSpacing: '2.5px',
          textTransform: 'uppercase',
          color: '#0CAECF',
          mb: 2
        }}
      >
        How it works
      </Typography>

      <Typography
        component="h2"
        variant="h3"
        fontFamily="Inter"
        sx={{
          fontWeight: 700,
          fontSize: { xs: '29px', sm: '29px', md: '41.8px' },
          lineHeight: '100%',
          letterSpacing: '0px',
          textAlign: 'center',
          mb: 4,
        }}
      >
        15 days from kickoff to first content live.
      </Typography>

      {/* Steps — alternating layout matching the original brands section */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: { xs: 2, md: 5 },
          [theme.breakpoints.down('md')]: { gap: '10px' }
        }}
      >
        {steps.map((step, index) => {
          const isEven = index % 2 === 0;
          return (
            <Box
              key={step.number}
              sx={{
                display: 'flex',
                width: '100%',
                maxWidth: { md: 880 },
                mx: 'auto',
                gap: '35px',
                alignItems: 'center',
                mt: 6,
                mb: 4,
                flexDirection: isEven ? 'row' : 'row-reverse',
                [theme.breakpoints.down('md')]: {
                  flexDirection: 'column',
                  width: '100%',
                  gap: '10px',
                  mt: 1,
                  mb: 1
                }
              }}
            >
              {/* Step number visual (square tile) */}
              <Box
                sx={{
                  flex: 'none',
                  width: '100%',
                  maxWidth: { xs: '240px', sm: '280px', md: '320px' },
                  aspectRatio: '1 / 1',
                  background: 'linear-gradient(139.56deg, rgba(12,174,207,0.12) -29.1%, rgba(88,15,107,0.12) 96.45%)',
                  border: '1px solid rgba(12, 174, 207, 0.2)',
                  borderRadius: '12px',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 1,
                  mx: { xs: 'auto', md: 0 }
                }}
              >
                <Typography
                  fontFamily="Inter"
                  sx={{
                    fontSize: { xs: '48px', md: '96px' },
                    fontWeight: 800,
                    color: 'rgba(12, 174, 207, 0.25)',
                    lineHeight: 1
                  }}
                >
                  {step.number}
                </Typography>
                <Typography
                  fontFamily="Inter"
                  sx={{
                    fontSize: '12px',
                    fontWeight: 600,
                    letterSpacing: '2px',
                    textTransform: 'uppercase',
                    color: '#0CAECF',
                  }}
                >
                  {step.week}
                </Typography>
              </Box>

              {/* Text side */}
              <Box
                sx={{
                  flex: 1,
                  minWidth: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  textAlign: 'left',
                  maxWidth: { xs: '100%', md: 500 },
                  px: { xs: 1, sm: 2, md: 0 }
                }}
              >
                <Typography
                  component="h3"
                  variant="h4"
                  fontFamily="Inter"
                  sx={{
                    fontWeight: 600,
                    fontSize: { xs: '18px', md: '24px', lg: '29px' },
                    lineHeight: '110%',
                    color: '#FFFFFF',
                    mb: 2,
                    [theme.breakpoints.down('md')]: {
                      textAlign: 'center',
                      lineHeight: '120%',
                    }
                  }}
                >
                  {step.title}
                </Typography>
                <Typography
                  fontFamily="Inter"
                  sx={{
                    fontWeight: 300,
                    fontSize: { xs: '15px', md: '16px' },
                    color: '#c0c0c0',
                    lineHeight: '150%',
                    maxWidth: '100%',
                    [theme.breakpoints.down('md')]: { textAlign: 'center' }
                  }}
                >
                  {step.description}
                </Typography>
              </Box>
            </Box>
          );
        })}
      </Box>

      {/* CTA */}
      <Box sx={{ mt: 6 }}>
        <Button
          component="a"
          href="https://outlook.office.com/bookwithme/user/eb7d87d7154e461ab27627de6f7d2e9e@saagas.ai?anonymous&ismsaljsauthenabled&ep=plink"
          target="_blank"
          rel="noopener noreferrer"
          sx={{
            px: isMdOrBelow ? '15px' : '32px',
            py: isMdOrBelow ? '8px' : '16px',
            maxWidth: isMdOrBelow ? '200px' : '260px',
            maxHeight: isMdOrBelow ? '49px' : '52px',
            borderRadius: '170px',
            boxShadow: '0px -4px 50px 0px rgba(11,87,208,0.68)',
            fontWeight: 700,
            fontSize: isMdOrBelow ? '14px' : '16px',
            background: 'linear-gradient(163deg, rgb(12, 174, 207) -29.1%, rgb(88, 15, 107) 96.45%)',
            color: 'white',
            textTransform: 'none',
            '&:hover': {
              background: 'linear-gradient(163deg, rgb(12, 160, 200) -29.1%, rgb(80, 10, 100) 96.45%)'
            }
          }}
        >
          Book a Discovery Call →
        </Button>
      </Box>
    </Box>
  );
};
